import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput } from "../../components/FormInput";
import ErrorBanner from "../../components/ErrorBanner";

import { useMutateCreateModelVersion } from "../../data/services/clientService";
import UserContext from "../../layouts/UserContext";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";

const DATA_ITEM_KEY = "clientId";

const AddModelVersion = ({ onModelClose, client }) => {
  const { clientId, oePeriodId } = useParams();
  const navigate = useNavigate();
  const { loggedInUser } = React.useContext(UserContext);

  const [addModelVersionSubmitted, setAddModelVersionSubmitted] =
    React.useState(false);
  const [applyToChildren, setApplyToChildren] = React.useState(false);
  const [selectedSubClients, setSelectedSubClients] = React.useState({});

  const useMutateCreateModelVersionHandler = useMutateCreateModelVersion();

  const onSelectionChange = (event) => {
    const id = event.dataItem[DATA_ITEM_KEY];
    setSelectedSubClients({
      ...selectedSubClients,
      [id]: !selectedSubClients[id],
    });
  };

  const onHeaderSelectionChange = (event) => {
    const checked = event.syntheticEvent.target.checked;
    const newSelection = {};
    if (checked) {
      client.subClients.forEach((item) => {
        newSelection[item[DATA_ITEM_KEY]] = true;
      });
    }
    setSelectedSubClients(newSelection);
  };

  const handleConfirmCreateModel = async (item) => {
    const selectedIds = Object.keys(selectedSubClients).filter(
      (id) => selectedSubClients[id],
    );

    let data = {
      clientId: clientId,
      oePeriodId: oePeriodId,
      modelName: item.modelVersionName,
      applyToChildren: applyToChildren,
      childClientIds: applyToChildren ? selectedIds : [],
    };

    setAddModelVersionSubmitted(true);

    let response = await useMutateCreateModelVersionHandler.mutateAsync(data);

    setAddModelVersionSubmitted(false);

    if (loggedInUser.hasAdminAppPrivilege) {
      navigate(
        `/admin/clients/${clientId}/oe/${oePeriodId}/ratemodel/${response.data.modelVersionId}`,
      );
    } else {
      navigate(
        `/clients/${clientId}/oe/${oePeriodId}/ratemodel/${response.data.modelVersionId}`,
      );
    }
  };

  const cancelCreateModel = () => {
    onModelClose();
  };

  // Hotfix: force-disabled until SOF-256 apply-to-children behavior is finalized.
  const hasSubClients = false;

  return (
    <Dialog
      title={"Create Contribution Model"}
      onClose={cancelCreateModel}
      width={400}
    >
      <ErrorBanner
        handler={useMutateCreateModelVersionHandler}
        onError={() => setAddModelVersionSubmitted(false)}
      />
      <Form
        onSubmit={handleConfirmCreateModel}
        render={(formRenderProps) => (
          <FormElement horizontal={false} style={{ maxWidth: 600 }}>
            <Field
              id={"modelVersionName"}
              name={"modelVersionName"}
              label={"Name"}
              autoFocus={true}
              component={FormInput}
              Placeholder={"Enter version name"}
              required={true}
              autoComplete={"off"}
              validator={(value) =>
                value ? "" : "Please enter a model version name"
              }
            />
            {hasSubClients && (
              <div style={{ marginTop: "20px" }}>
                <Checkbox
                  label={"This model will apply to child clients"}
                  checked={applyToChildren}
                  onChange={(e) => setApplyToChildren(e.value)}
                />

                {applyToChildren && (
                  <div
                    style={{
                      marginTop: "15px",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                  >
                    <p style={{ fontSize: "12px", fontWeight: "bold" }}>
                      Selected Child Clients:
                    </p>
                    <Grid
                      data={client.subClients.map((item) => ({
                        ...item,
                        selected: !!selectedSubClients[item[DATA_ITEM_KEY]],
                      }))}
                      selectedField="selected"
                      dataItemKey={DATA_ITEM_KEY}
                      onSelectionChange={onSelectionChange}
                      onHeaderSelectionChange={onHeaderSelectionChange}
                    >
                      <Column
                        field="selected"
                        width="50px"
                        headerSelectionValue={
                          client.subClients.length > 0 &&
                          client.subClients.every(
                            (item) => selectedSubClients[item[DATA_ITEM_KEY]],
                          )
                        }
                      />
                      <Column field="clientId" title="ID" width="100px" />
                      <Column field="clientName" title="Client Name" />
                    </Grid>
                  </div>
                )}
              </div>
            )}
            <div style={{ paddingTop: 40 }}></div>
            <DialogActionsBar layout={"end"}>
              <Button
                onClick={cancelCreateModel}
                disabled={addModelVersionSubmitted}
              >
                {"Cancel"}
              </Button>
              <Button
                type={"submit"}
                themeColor={"primary"}
                disabled={
                  !formRenderProps.allowSubmit || addModelVersionSubmitted
                }
              >
                {"Create"}
              </Button>
            </DialogActionsBar>
          </FormElement>
        )}
      />
    </Dialog>
  );
};

export default AddModelVersion;
